import Logger from "../lib/logger";
import Player from "@vimeo/player";
import gsap from "gsap";
import event from "../lib/mixins/event";

class HeroWithIntro {
	constructor(dom) {
		this.NAME = "HeroWithIntro";
		this.dom = dom;
		this.triggerDom = document.querySelector("[data-hero-intro-video-trigger]");
		this.overlayDom = document.querySelector("[data-hero-intro-video-overlay]");
		this.preloaderDom = dom.querySelector("[data-hero-intro-preloader]");
		this.fillDom = dom.querySelector("[data-hero-intro-fill]");
		this.placeholderDom = dom.querySelector("[data-hero-intro-placeholder]");
		this.videoDom = dom.querySelector("[data-hero-intro-video]");
		this.expandIconDom = dom.querySelector(".hero-with-intro__expand-icon");
	}

	init() {
		Logger.info(`${this.NAME} > init`);

		if (this.videoDom && this.preloaderDom) {
			this.initPreloader();
		} else {
			// No preloader intro to play (e.g. image variant) — nothing to wait for
			this.trigger("done");
		}

		if (!this.triggerDom || !this.overlayDom) return;

		// Move overlay to <body> so it escapes theme-main's stacking context
		document.body.appendChild(this.overlayDom);

		const iframeDom = this.overlayDom.querySelector("iframe");
		if (!iframeDom) return;

		// Lazy-load plugins swap src → data-src; restore it before Vimeo SDK init
		if (iframeDom.dataset.src) {
			iframeDom.src = iframeDom.dataset.src;
		}

		// Logger.info(`${this.NAME} > iframe src:`, iframeDom.src, iframeDom.outerHTML.slice(0, 200));

		let player;
		try {
			player = new Player(iframeDom, {});
		} catch (e) {
			Logger.info(`${this.NAME} > Vimeo player could not be initialised`, e);
			iframeDom.insertAdjacentHTML(
				"afterend",
				`
				<div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;">
					<p style="color:#fff;font-family:sans-serif;font-size:18px;line-height:1.6;text-align:center;max-width:400px;opacity:0.8;background:black;padding:20px;">
						Video preview unavailable in this environment.
					</p>
				</div>
			`
			);
		}

		this.triggerDom.addEventListener("click", (e) => {
			e.preventDefault();
			this.overlayDom.classList.add("hero-with-intro__overlay--active");
			if (player) player.play();
		});

		this.overlayDom.addEventListener("click", (e) => {
			e.preventDefault();
			this.overlayDom.classList.remove("hero-with-intro__overlay--active");
			if (player) {
				player.pause();
				player.setCurrentTime(0);
			}
		});
	}

	initPreloader() {
		const { fillDom, placeholderDom, videoDom, preloaderDom } = this;

		// Where the photo settles on the table — subtle random tilt each load
		const landRotation =
			(Math.random() < 0.5 ? -1 : 1) * (2 + Math.random() * 1.5);
		// Toss rotation: pronounced, random direction independent of land tilt
		const tossRotation =
			(Math.random() < 0.5 ? -1 : 1) * (12 + Math.random() * 8);
		// Come in from below, random left or right
		const tossX = (Math.random() < 0.5 ? -1 : 1) * (60 + Math.random() * 50);

		// Hidden until the toss begins — preloader sits on top, but opacity:0 ensures
		// nothing bleeds through if stacking contexts shift
		// zIndex above preloader (10) so polaroid sits on top as it comes in
		gsap.set(placeholderDom, {
			scale: 0.5,
			rotation: tossRotation,
			x: tossX,
			y: 200,
			opacity: 0,
			filter: "blur(0px)",
			zIndex: 11,
			// Values are large to compensate for scale compression —
			// box-shadow is scaled with the element, so at scale(0.5) a
			// 100px offset renders as ~50px visually
			boxShadow: "0px 100px 80px -10px rgba(0,0,0,0.55)",
			transformOrigin: "50% 50%",
		});

		const state = { progress: 0 };

		const setFill = (p) => {
			const pct = ((1 - p) * 100).toFixed(2);
			fillDom.style.clipPath = `inset(${pct}% 0 0 0)`;
		};

		const { expandIconDom } = this;

		// Pre-calculate roll distance and rotation so the icon moves like a real wheel
		let iconRollDistance = 0;
		let iconRollDegrees = 0;
		if (expandIconDom) {
			const iconSize = expandIconDom.offsetWidth || 48;
			iconRollDistance = window.innerWidth + iconSize;
			iconRollDegrees =
				Math.round(((iconRollDistance / (Math.PI * iconSize)) * 360) / 360) *
				360;
			gsap.set(expandIconDom, { x: iconRollDistance, zIndex: 20 });
		}

		const revealPlaceholder = () => {
			// Preloader stays visible — polaroid sits on top via zIndex: 11
			preloaderDom.style.pointerEvents = "none";

			const tl = gsap.timeline({ delay: 0.1 });

			// ── Land: photo slides in from below/side and settles on the surface
			// Shadow collapses from large/diffuse (elevated) to tight (on surface)
			tl.to(placeholderDom, {
				scale: 0.3,
				rotation: landRotation,
				x: 10,
				y: 6,
				opacity: 1,
				boxShadow: "0px 55px 40px -8px rgba(0,0,0,0.65)",
				duration: 0.45,
				ease: "power3.out",
			})
				// ── Slide: momentum carries it across the surface
				.to(placeholderDom, {
					x: 20,
					y: 13,
					rotation: landRotation * 0.65,
					duration: 0.28,
					ease: "power3.out",
				})
				// ── Settle: friction stops it, shadow fully grounded
				.to(placeholderDom, {
					x: 14,
					y: 9,
					rotation: landRotation,
					boxShadow: "0px 30px 35px -5px rgba(0,0,0,0.7)",
					duration: 0.22,
					ease: "power2.out",
				})
				// ── Dive: we move toward it — slow lean that becomes a fall
				.to(placeholderDom, {
					scale: 1.38,
					x: 0,
					y: 0,
					rotation: 0,
					filter: "blur(2px)",
					duration: 1.25,
					ease: "power3.in",
					delay: 0.32,
				})
				// Nav starts sliding in here — while the final settle (and any
				// icon roll-in below) is still playing, so it arrives sooner
				.call(() => this.trigger("done"))
				// ── Arrive: world catches us, everything settles
				.to(placeholderDom, {
					scale: 1.0,
					filter: "blur(0px)",
					duration: 0.5,
					ease: "power2.out",
				});

			if (expandIconDom) {
				// Roll in like a wheel — x and rotation use the same ease so
				// translational and rotational velocity stay proportional (no slip)
				tl.to(expandIconDom, {
					x: 0,
					rotation: -iconRollDegrees,
					duration: 0.85,
					ease: "power2.out",
				});
			}
		};

		let simulatedTween;
		let finished = false;

		const finish = () => {
			if (finished) return;
			finished = true;
			if (simulatedTween) simulatedTween.kill();

			gsap.to(state, {
				progress: 1,
				duration: 0.5,
				ease: "power2.inOut",
				onUpdate: () => setFill(state.progress),
				onComplete: revealPlaceholder,
			});
		};

		// Simulate progress to 82% — feels smooth on any connection speed
		simulatedTween = gsap.to(state, {
			progress: 0.82,
			duration: 2.2,
			ease: "power2.out",
			onUpdate: () => setFill(state.progress),
		});

		// Real video readiness events
		videoDom.addEventListener("canplay", finish, { once: true });

		// Already cached / fast load
		if (videoDom.readyState >= 3) finish();

		// Hard fallback after 10s in case events never fire
		setTimeout(finish, 10000);
	}
}

Object.assign(HeroWithIntro.prototype, event);

export default HeroWithIntro;
